import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SeatStepper } from '@/features/billing/components/SeatStepper';
import { formatPlanPeriodDate } from '@/features/billing/lib/formatPlanPeriodDate';
import {
  BUSINESS_INCLUDED_SEATS,
  BUSINESS_SEAT_PACK_SIZE,
  businessSeatLimit,
  MAX_SEAT_PACKS,
} from '@/features/billing/lib/seatPacks';
import type { Organization } from '@/types/gql.generated';
import { useToast, Box, Button, Divider, Flex, Text } from '@/ui';
import { isGQLErrorOfType } from '@/utils/errors';
import { useAddSeatPacks } from '../hooks/useAddSeatPacks';
import { useCancelSeatPackRemoval } from '../hooks/useCancelSeatPackRemoval';
import { useScheduleSeatPackRemoval } from '../hooks/useScheduleSeatPackRemoval';

type Props = {
  organization: Organization;
  /**
   * Seats currently occupied: accepted members + live (non-expired) pending
   * invites — the same count the server's removal gate uses.
   */
  occupiedSeats: number;
  onDone: () => void;
};

const packsForSeats = (seats: number) =>
  (seats - BUSINESS_INCLUDED_SEATS) / BUSINESS_SEAT_PACK_SIZE;

// Picks a new *total* seat count rather than a number of packs to add or
// remove. The direction of the change decides what actually happens: growing
// bills a prorated charge immediately, shrinking is scheduled for renewal (and
// the server blocks it until members and pending invites fit the lower limit).
export const SeatAdjustPanel = ({
  organization,
  occupiedSeats,
  onDone,
}: Props) => {
  const { t } = useTranslation('settingsModal', {
    keyPrefix: 'organizations.seats',
  });
  const toast = useToast();

  const currentPacks = organization.seatPacks ?? 0;
  const currentSeats = businessSeatLimit(currentPacks);
  const hasPendingDecrease = organization.pendingSeatPacks != null;

  const [seats, setSeats] = useState(currentSeats);

  const { addSeatPacks, isPending: isAdding } = useAddSeatPacks(
    organization.id
  );
  const { scheduleSeatPackRemoval, isPending: isScheduling } =
    useScheduleSeatPackRemoval(organization.id);
  const { cancelSeatPackRemoval, isPending: isCanceling } =
    useCancelSeatPackRemoval(organization.id);

  const formattedPeriodEnd = formatPlanPeriodDate(
    organization.subscriptionCurrentPeriodEnd
  );

  const packDelta = packsForSeats(seats) - currentPacks;
  // The server's gate, restated: a seat total below what's already occupied
  // can't be honoured until members or pending invites go away.
  const isBelowMembership = seats < occupiedSeats;

  const handleSubmit = () => {
    if (packDelta > 0) {
      addSeatPacks(packDelta, {
        onSuccess: () => {
          toast.notify(
            t('adjust.add_success', {
              seats: packDelta * BUSINESS_SEAT_PACK_SIZE,
            })
          );
          onDone();
        },
        onError: (err) => {
          toast.error(
            isGQLErrorOfType(err, 'OrganizationSeatPackPaymentFailed')
              ? t('adjust.add_error_payment')
              : isGQLErrorOfType(err, 'OrganizationSeatPackChangePending')
                ? t('adjust.add_error_pending')
                : t('adjust.add_error')
          );
        },
      });
      return;
    }

    scheduleSeatPackRemoval(-packDelta, {
      onSuccess: () => {
        toast.notify(t('adjust.schedule_success'));
        onDone();
      },
      onError: (err) => {
        toast.error(
          isGQLErrorOfType(err, 'OrganizationSeatPackRemovalBlocked')
            ? t('adjust.blocked')
            : t('adjust.schedule_error')
        );
      },
    });
  };

  const handleCancelPending = () => {
    cancelSeatPackRemoval({
      onSuccess: () => toast.notify(t('adjust.pending_keep_success')),
      onError: () => toast.error(t('adjust.pending_keep_error')),
    });
  };

  if (hasPendingDecrease) {
    return (
      <Flex bg="gray.100" borderRadius="ah-xl" direction="column" gap="4" p="5">
        <Text fontWeight="medium">
          {t('adjust.pending_banner', {
            seats: organization.pendingSeatLimit,
            date: formattedPeriodEnd,
          })}
        </Text>
        <Divider />
        <Flex align="center" gap="4">
          <Button
            isLoading={isCanceling}
            variant="secondary"
            onClick={handleCancelPending}
          >
            {t('adjust.pending_keep')}
          </Button>
          <Button variant="link" onClick={onDone}>
            {t('adjust.cancel')}
          </Button>
        </Flex>
      </Flex>
    );
  }

  const targetPacks = packsForSeats(seats);
  const composition =
    targetPacks > 0
      ? t('adjust.composition_packs', {
          count: targetPacks,
          included: BUSINESS_INCLUDED_SEATS,
          packSize: BUSINESS_SEAT_PACK_SIZE,
        })
      : t('adjust.composition_base', { included: BUSINESS_INCLUDED_SEATS });

  return (
    <Flex bg="gray.100" borderRadius="ah-xl" direction="column" gap="4" p="5">
      <Flex align="center" gap="4" justify="space-between">
        <Box>
          <Text fontWeight="medium">{t('adjust.heading')}</Text>
          <Text color="customgray.mid" fontSize="sm">
            {composition}
          </Text>
        </Box>
        <SeatStepper
          max={businessSeatLimit(MAX_SEAT_PACKS)}
          min={businessSeatLimit(0)}
          step={BUSINESS_SEAT_PACK_SIZE}
          value={seats}
          decreaseLabel={t('adjust.decrease', {
            count: BUSINESS_SEAT_PACK_SIZE,
          })}
          increaseLabel={t('adjust.increase', {
            count: BUSINESS_SEAT_PACK_SIZE,
          })}
          onChange={setSeats}
        />
      </Flex>

      {isBelowMembership && (
        <Box
          bg="yellow.50"
          borderColor="yellow.400"
          borderRadius="ah-lg"
          borderWidth="1px"
          p="3"
        >
          <Text color="destructive.600" fontSize="sm" fontWeight="semibold">
            {t('adjust.error_title')}
          </Text>
          <Text color="destructive.500" fontSize="sm">
            {t('adjust.error_body', {
              count: occupiedSeats,
              excess: occupiedSeats - seats,
              seats,
            })}
          </Text>
        </Box>
      )}

      <Divider />

      <Text color="customgray.mid" fontSize="sm">
        {isBelowMembership
          ? t('adjust.note_invalid', { seats: occupiedSeats })
          : packDelta > 0
            ? t('adjust.note_increase', {
                seats: packDelta * BUSINESS_SEAT_PACK_SIZE,
              })
            : packDelta < 0
              ? t('adjust.note_decrease', { date: formattedPeriodEnd })
              : t('adjust.note_unchanged')}
      </Text>

      <Flex align="center" gap="4">
        <Button
          isDisabled={packDelta === 0 || isBelowMembership}
          isLoading={isAdding || isScheduling}
          onClick={handleSubmit}
        >
          {packDelta > 0 ? t('adjust.submit_add') : t('adjust.submit_schedule')}
        </Button>
        <Button variant="link" onClick={onDone}>
          {t('adjust.cancel')}
        </Button>
      </Flex>
    </Flex>
  );
};
